import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Search, X, AlertTriangle, CheckSquare, Square } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageShell } from "../components/Layout";
import CelebrityAvatar from "../components/CelebrityAvatar";

/**
 * Super-admin tool — Delete celebrities from the database.
 *
 * Flow:
 *  1. Pick a category.
 *  2. Server returns every celeb in that category (hardcoded + custom)
 *     tagged with a `source` field.
 *  3. Admin filters via search, ticks the ones to delete, clicks
 *     "Delete selected".
 *  4. UI shows a confirm modal, then calls the bulk-delete endpoint.
 *
 * Hardcoded celebs (source === "hardcoded") are shown READ-ONLY — their
 * checkbox is disabled and a lock badge is rendered — because they live
 * in celebrities.py, not the DB. Deleting them requires a code change.
 */

export default function AdminDeleteCelebrityPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, hardcoded: 0, custom: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Only super-admin can be on this page. The backend also enforces it,
  // but bouncing here keeps the URL from being a UX dead-end.
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/", { replace: true }); return; }
    if (!user.is_super_admin) { navigate("/", { replace: true }); return; }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    api.get("/celebrities/categories")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const loadCategory = async (slug) => {
    if (!slug) {
      setItems([]);
      setCounts({ total: 0, hardcoded: 0, custom: 0 });
      return;
    }
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/admin/celebrities/manage/${slug}`);
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, hardcoded: 0, custom: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load celebrities.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onPickCategory = (slug) => {
    setCategory(slug);
    setQ("");
    loadCategory(slug);
  };

  // Client-side filter. Case-insensitive substring across name/company/
  // industry/country/slug so the admin can find anything they added.
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((c) => {
      const hay = `${c.name} ${c.company} ${c.industry} ${c.country} ${c.slug}`.toLowerCase();
      return hay.includes(query);
    });
  }, [items, q]);

  // Only deletable rows in the current filtered view — for "select all".
  const filteredDeletable = useMemo(
    () => filtered.filter((c) => c.source === "custom"),
    [filtered],
  );

  const allDeletableSelected =
    filteredDeletable.length > 0 &&
    filteredDeletable.every((c) => selected.has(c.slug));

  const toggleRow = (slug, source) => {
    if (source !== "custom") return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (allDeletableSelected) {
        // Deselect every currently-visible deletable row.
        const next = new Set(prev);
        for (const c of filteredDeletable) next.delete(c.slug);
        return next;
      }
      // Select every currently-visible deletable row (union with existing).
      const next = new Set(prev);
      for (const c of filteredDeletable) next.add(c.slug);
      return next;
    });
  };

  const doDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/celebrities/bulk-delete", {
        slugs: Array.from(selected),
      });
      toast.success(`Deleted ${data.deleted_count} celebrit${data.deleted_count === 1 ? "y" : "ies"}.`);
      setSelected(new Set());
      setConfirmOpen(false);
      // Refresh the list so the row disappears without a full reload.
      loadCategory(category);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageShell>
      <main className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider text-stone-500 hover:text-[#3F2D1B] mb-6"
          data-testid="delete-celebrity-back"
        >
          <ArrowLeft size={14} /> BACK
        </button>

        <div className="label-eyebrow text-rose-700 flex items-center gap-2">
          <Trash2 size={12} /> SUPER-ADMIN · DELETE CELEBRITIES
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 font-light leading-tight text-[#3F2D1B]">
          Manage the celebrity database.
        </h1>
        <p className="text-stone-500 text-sm mt-3 max-w-2xl font-light">
          Pick a category to see every celebrity in it. Tick the rows you want to
          remove and click <strong>Delete selected</strong>. Hardcoded catalog entries
          are read-only — they live in <code>celebrities.py</code>, not the DB.
        </p>

        {/* Category picker */}
        <div className="mt-8 grid sm:grid-cols-[280px_1fr] gap-4">
          <div>
            <label className="text-[11px] font-bold tracking-wider text-stone-600 uppercase">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => onPickCategory(e.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-stone-300 text-[#3F2D1B] bg-white focus:border-[#FFB800] focus:outline-none"
              data-testid="delete-celeb-category"
            >
              <option value="">— Select category —</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.label} ({c.count})
                </option>
              ))}
            </select>
          </div>

          {category && (
            <div>
              <label className="text-[11px] font-bold tracking-wider text-stone-600 uppercase">
                Search in list
              </label>
              <div className="mt-1.5 flex items-center gap-2 px-3 py-2.5 rounded-xl border border-stone-300 bg-white focus-within:border-[#FFB800]">
                <Search size={14} className="text-stone-500 flex-shrink-0" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, company, industry, slug…"
                  className="flex-1 bg-transparent outline-none text-sm"
                  data-testid="delete-celeb-search"
                />
                {q && (
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    className="text-stone-400 hover:text-stone-700"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Counts + bulk actions */}
        {category && !loading && (
          <div
            className="mt-6 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3"
            data-testid="delete-celeb-summary"
          >
            <span className="text-sm text-[#3F2D1B]">
              Showing <strong>{filtered.length}</strong> of <strong>{counts.total}</strong>
              {" "}(<span className="text-emerald-700">{counts.custom} deletable</span>,
              {" "}<span className="text-stone-500">{counts.hardcoded} hardcoded</span>)
            </span>
            {filteredDeletable.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider text-stone-700 hover:text-[#3F2D1B]"
                data-testid="delete-celeb-toggle-all"
              >
                {allDeletableSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                {allDeletableSelected ? "UNSELECT ALL" : "SELECT ALL VISIBLE"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold tracking-wider hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-celeb-bulk-btn"
            >
              <Trash2 size={12} /> DELETE SELECTED ({selected.size})
            </button>
          </div>
        )}

        {/* List */}
        {loading && (
          <div className="mt-8 text-center text-sm text-stone-500 py-10" data-testid="delete-celeb-loading">
            Loading celebrities…
          </div>
        )}

        {!loading && category && filtered.length === 0 && (
          <div className="mt-8 text-center text-sm text-stone-500 py-10" data-testid="delete-celeb-empty">
            {q ? `No celebrity matched "${q}" in this category.` : "This category has no entries yet."}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <ul className="mt-6 space-y-2" data-testid="delete-celeb-list">
            {filtered.map((c) => {
              const isCustom = c.source === "custom";
              const isSelected = selected.has(c.slug);
              return (
                <li
                  key={c.slug}
                  className={`rounded-xl border transition-all p-3 flex items-center gap-3 ${
                    isSelected
                      ? "border-rose-300 bg-rose-50/60"
                      : isCustom
                      ? "border-stone-200 bg-white hover:border-stone-300"
                      : "border-stone-100 bg-stone-50/60"
                  }`}
                  data-testid={`delete-celeb-row-${c.slug}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleRow(c.slug, c.source)}
                    disabled={!isCustom}
                    className="flex-shrink-0 disabled:cursor-not-allowed"
                    aria-label={isCustom ? "Toggle selection" : "Hardcoded (read-only)"}
                    data-testid={`delete-celeb-check-${c.slug}`}
                  >
                    {isSelected ? (
                      <CheckSquare size={20} className="text-rose-600" />
                    ) : (
                      <Square size={20} className={isCustom ? "text-stone-400" : "text-stone-200"} />
                    )}
                  </button>

                  <CelebrityAvatar src={c.photo} name={c.name} size={44} className="ring-2 ring-white shadow flex-shrink-0" />

                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#3F2D1B] truncate">
                      {c.name}
                      {c.malformed && (
                        <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-200 text-rose-900">
                          MALFORMED
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-stone-500 truncate">
                      {c.company || c.industry || c.country} · <code className="font-mono">{c.slug}</code>
                    </div>
                  </div>

                  {c.rodden_rating && (
                    <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded flex-shrink-0 ${
                      c.rodden_rating === "AA" ? "bg-emerald-100 text-emerald-800" :
                      c.rodden_rating === "A"  ? "bg-sky-100 text-sky-800" :
                      c.rodden_rating === "B"  ? "bg-amber-100 text-amber-800" :
                                                  "bg-stone-100 text-stone-700"
                    }`}>
                      {c.rodden_rating}
                    </span>
                  )}

                  {isCustom ? (
                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 flex-shrink-0">
                      CUSTOM
                    </span>
                  ) : (
                    <span
                      className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-stone-200 text-stone-600 flex-shrink-0"
                      title="Hardcoded entry — edit celebrities.py to remove"
                    >
                      HARDCODED
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="delete-celeb-confirm-modal"
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-rose-200 overflow-hidden">
            <div className="p-5 border-b border-stone-200 flex items-start gap-3">
              <AlertTriangle size={28} className="text-rose-600 flex-shrink-0" />
              <div>
                <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">
                  Delete {selected.size} celebrit{selected.size === 1 ? "y" : "ies"}?
                </h3>
                <p className="text-xs text-stone-500 mt-1">
                  This action is permanent. Their birth data, cached signatures and rise-event analyses will be removed from the database.
                </p>
              </div>
            </div>
            <div className="p-5 max-h-64 overflow-y-auto">
              <ul className="space-y-1 text-sm text-stone-700">
                {Array.from(selected).slice(0, 20).map((slug) => {
                  const row = items.find((r) => r.slug === slug);
                  return <li key={slug}>· {row?.name || slug}</li>;
                })}
                {selected.size > 20 && (
                  <li className="text-stone-500 italic">…and {selected.size - 20} more.</li>
                )}
              </ul>
            </div>
            <div className="p-5 pt-0 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-full text-stone-700 text-xs font-bold tracking-wider hover:bg-stone-100"
                data-testid="delete-celeb-cancel"
              >
                CANCEL
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-rose-600 text-white text-xs font-bold tracking-wider hover:bg-rose-700 disabled:opacity-50"
                data-testid="delete-celeb-confirm"
              >
                {deleting ? "DELETING…" : "YES, DELETE"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
